import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../contexts/TenantContext';
import { usePermissionGuard, ACTIONS, RESOURCES } from '../utils/permissionGuard';
import { useState, useEffect } from 'react';

/**
 * Custom hook that combines Auth0 user data with tenant context
 * and provides permission checking utilities
 */
export function useUserPermissions() {
  const { user: auth0User, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { currentOrganization } = useTenant();
  const [userRole, setUserRole] = useState(null);

  // Create a state to store accessible school IDs from JWT
  const [accessibleSchoolIds, setAccessibleSchoolIds] = useState([]);

  // Extract role and accessible school IDs from JWT token
  useEffect(() => {
    const extractDataFromToken = async () => {
      if (!isAuthenticated || !auth0User) {
        setUserRole(null);
        setAccessibleSchoolIds([]);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const payload = JSON.parse(atob(token.split('.')[1]));
        const role = payload['https://arcattend.com/role'] || null;
        const schoolIds = payload['https://arcattend.com/accessible_school_ids'] || [];
        
        setUserRole(role);
        setAccessibleSchoolIds(schoolIds);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error extracting data from JWT token:', error);
        }
        setUserRole(null);
        setAccessibleSchoolIds([]);
      }
    };

    extractDataFromToken();
  }, [isAuthenticated, auth0User, getAccessTokenSilently]);

  // Create a complete user object with role and scope information
  const user = {
    ...auth0User,
    role: userRole,
    organization_id: currentOrganization?.id,
    schoolId: auth0User?.['https://arcattend.com/school_id'], // For school-scoped users
  };



  // Get permission utilities
  const permissions = usePermissionGuard(user);

  return {
    user,
    permissions,
    // Convenience methods for common permission checks
    canCreateSchool: () => permissions.hasPermission(RESOURCES.SCHOOL, ACTIONS.CREATE, {
      organization_id: currentOrganization?.id
    }),
    canDeleteSchool: () => permissions.hasPermission(RESOURCES.SCHOOL, ACTIONS.DELETE, {
      organization_id: currentOrganization?.id
    }),
    canInviteUsers: () => permissions.hasPermission(RESOURCES.INVITATIONS, ACTIONS.CREATE),
    canManageUsers: () => permissions.hasPermission(RESOURCES.USER, ACTIONS.REMOVE),
    canDeleteOrganization: () => permissions.hasPermission(RESOURCES.ORGANIZATION, ACTIONS.DELETE, {
      organization_id: currentOrganization?.id
    }),
    canTakeAttendance: () => permissions.hasPermission(RESOURCES.ATTENDANCE, ACTIONS.TAKE_ATTENDANCE),
    
    // Check if current user can manage a specific target user based on school scope
    canManageSpecificUser: (targetUser) => {
      // Check basic permission first
      if (!permissions.hasPermission(RESOURCES.USER, ACTIONS.REMOVE)) {
        return false;
      }

      // Prevent self-management
      if (targetUser.email === auth0User?.email || targetUser.authId === auth0User?.sub) {
        return false;
      }

      // Organization-wide users can manage any user (except themselves)
      if (permissions.hasOrganizationAccess(RESOURCES.USER, ACTIONS.REMOVE)) {
        return true;
      }

      // School-scoped permission checking
      if (userRole === 'school_admin' || userRole === 'teacher') {
        const currentUserSchools = accessibleSchoolIds;
        const targetUserSchools = targetUser.accessibleSchoolIds || [];
        
        // Debug logging for development
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 School scope check:', {
            user: targetUser.name,
            currentRole: userRole,
            currentSchools: currentUserSchools,
            targetRole: targetUser.role,
            targetSchools: targetUser.accessibleSchoolIds
          });
        }

        // Target user with organization-wide access cannot be managed by school-scoped users
        if (targetUserSchools.length === 0) {
          return false;
        }

        // Current user must have school access to manage school-scoped users
        if (currentUserSchools.length === 0) {
          return false;
        }

        // Check for school overlap
        const hasOverlap = currentUserSchools.some(schoolId => 
          targetUserSchools.includes(schoolId)
        );

        if (process.env.NODE_ENV === 'development') {
          console.log(`🔍 School overlap: ${hasOverlap ? '✅' : '❌'}`);
        }
        return hasOverlap;
      }

      return false;
    },
    
    // Role-based helpers
    isTenantAdmin: () => userRole === 'tenant_administrator',
    isOrgAdmin: () => userRole === 'organization_admin',
    isSchoolAdmin: () => userRole === 'school_admin',
    isTeacher: () => userRole === 'teacher' || userRole === 'organization_teacher',
    
    // School access helpers
    getAccessibleSchoolIds: () => {
      return accessibleSchoolIds;
    },
    
    canAccessSchool: (schoolId) => {
      // Organization-wide roles can access any school
      if (permissions.hasOrganizationAccess(RESOURCES.SCHOOL, ACTIONS.READ)) {
        return true;
      }
      
      // School-scoped users can only access schools in their accessible_school_ids
      return accessibleSchoolIds.includes(schoolId);
    },
    
    hasOrganizationWideAccess: () => {
      // Check if user has organization-wide access (tenant_admin or organization_admin)
      return userRole === 'tenant_administrator' || userRole === 'organization_admin';
    },
    
    isSchoolScopedUser: () => {
      // Returns true if user is scoped to specific schools
      return userRole === 'school_admin' || userRole === 'teacher';
    },
    
    // Get available roles for invitations based on user's permissions
    getAvailableInviteRoles: () => {
      // Define all possible roles with their hierarchy
      const allRoles = [
        { value: 'tenant_administrator', label: 'Tenant Administrator', level: 5 },
        { value: 'organization_admin', label: 'Organization Admin', level: 4 },
        { value: 'organization_teacher', label: 'Organization Teacher', level: 3 },
        { value: 'organization_staff', label: 'Organization Staff', level: 3 },
        { value: 'school_admin', label: 'School Admin', level: 2 },
        { value: 'teacher', label: 'Teacher', level: 1 },
      ];
      
      // Get current user's role level
      const getCurrentUserLevel = () => {
        switch (userRole) {
          case 'tenant_administrator': return 5;
          case 'organization_admin': return 4;
          case 'organization_teacher': return 3;
          case 'organization_staff': return 3;
          case 'school_admin': return 2;
          case 'teacher': return 1;
          default: return 0;
        }
      };
      
      const currentUserLevel = getCurrentUserLevel();
      
      // Users can only invite roles at their level or below (excluding themselves)
      const availableRoles = allRoles.filter(role => {
        // Cannot invite same or higher level roles
        if (role.level >= currentUserLevel) {
          return false;
        }
        
        // Special cases for role-specific restrictions
        if (userRole === 'school_admin') {
          // School admins can only invite teachers for their schools
          return role.value === 'teacher';
        }
        
        if (userRole === 'teacher') {
          // Teachers cannot invite anyone
          return false;
        }
        
        return true;
      });
      
      // Sort by level descending (higher roles first)
      return availableRoles.sort((a, b) => b.level - a.level);
    },
    
    // Additional utility functions
    canManageSchool: (schoolId) => {
      // Check if user can manage a specific school
      if (permissions.hasOrganizationAccess(RESOURCES.SCHOOL, ACTIONS.UPDATE)) {
        return true;
      }
      
      // School-scoped users can only manage schools they have access to
      if (userRole === 'school_admin') {
        return accessibleSchoolIds.includes(schoolId);
      }
      
      return false;
    },
    
    canCreateUsersForSchool: (schoolId) => {
      // Check if user can create users for a specific school
      if (!permissions.hasPermission(RESOURCES.INVITATIONS, ACTIONS.CREATE)) {
        return false;
      }
      
      // Organization-wide users can create users for any school
      if (permissions.hasOrganizationAccess(RESOURCES.INVITATIONS, ACTIONS.CREATE)) {
        return true;
      }
      
      // School-scoped users can only create users for their accessible schools
      return accessibleSchoolIds.includes(schoolId);
    },
    
    // Get user's scope description for display purposes
    getUserScopeDescription: () => {
      if (userRole === 'tenant_administrator') {
        return 'All Organizations';
      }
      
      if (userRole === 'organization_admin') {
        return currentOrganization?.name || 'Current Organization';
      }
      
      if (userRole === 'school_admin' || userRole === 'teacher') {
        if (accessibleSchoolIds.length === 0) {
          return 'No School Access';
        } else if (accessibleSchoolIds.length === 1) {
          return 'Single School';
        } else {
          return `${accessibleSchoolIds.length} Schools`;
        }
      }
      
      return 'Unknown Scope';
    }
  };
}
